/**
 * Task Section Types
 *
 * UI types sit on FHIR R4 Task. Status and priority labels follow
 * docs/prd/Tasks/PRD_TASK_LIFECYCLE.md (Open/Completed/Cancelled and Low–Critical).
 */

import type { Reference, Task } from '@medplum/fhirtypes';

/**
 * FHIR Task.status. Producers emit requested | completed | cancelled only.
 * Other R4 values may appear on leftover data and display as Open.
 */
export type TaskStatus =
  | 'draft'
  | 'requested'
  | 'received'
  | 'accepted'
  | 'rejected'
  | 'ready'
  | 'cancelled'
  | 'in-progress'
  | 'on-hold'
  | 'failed'
  | 'completed'
  | 'entered-in-error';

/** Statuses the Tasks inbox shows and filters. */
export type TaskInboxStatus = 'requested' | 'completed' | 'cancelled';

/** FHIR Task.priority: routine / urgent / asap / stat → Low / Medium / High / Critical. */
export type TaskPriority = 'routine' | 'urgent' | 'asap' | 'stat';

/** Map any stored FHIR status onto the PRD inbox statuses. */
export function toInboxStatus(status: string | undefined): TaskInboxStatus {
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  return 'requested';
}

export const TaskStatusLabels: Record<TaskStatus, string> = {
  draft: 'Open',
  requested: 'Open',
  received: 'Open',
  accepted: 'Open',
  rejected: 'Open',
  ready: 'Open',
  cancelled: 'Cancelled',
  'in-progress': 'Open',
  'on-hold': 'Open',
  failed: 'Open',
  completed: 'Completed',
  'entered-in-error': 'Open',
};

export const TaskInboxStatusLabels: Record<TaskInboxStatus, string> = {
  requested: 'Open',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const TaskPriorityLabels: Record<TaskPriority, string> = {
  routine: 'Low',
  urgent: 'Medium',
  asap: 'High',
  stat: 'Critical',
};

/** Lower rank = higher urgency (Critical first when sorting ascending). */
export const TASK_PRIORITY_RANK: Record<TaskPriority, number> = {
  stat: 0,
  asap: 1,
  urgent: 2,
  routine: 3,
};

export const TaskStatusColors: Record<TaskInboxStatus, string> = {
  requested: 'blue',
  cancelled: 'gray',
  completed: 'green',
};

export const TaskPriorityColors: Record<TaskPriority, string> = {
  routine: 'gray',
  urgent: 'yellow',
  asap: 'orange',
  stat: 'red',
};

export const TASK_STATUS_FILTER_OPTIONS: { value: TaskInboxStatus; label: string }[] = [
  { value: 'requested', label: TaskInboxStatusLabels.requested },
  { value: 'completed', label: TaskInboxStatusLabels.completed },
  { value: 'cancelled', label: TaskInboxStatusLabels.cancelled },
];

export const TASK_PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'routine', label: TaskPriorityLabels.routine },
  { value: 'urgent', label: TaskPriorityLabels.urgent },
  { value: 'asap', label: TaskPriorityLabels.asap },
  { value: 'stat', label: TaskPriorityLabels.stat },
];

export interface TaskFilters {
  /** Provider filter: [] = no tasks; ['mine'] = my tasks; ['team'] = my team; or practitioner refs */
  provider: string[];
  /** Task type filter: [] = all; or task type codes */
  taskType: string[];
  view: 'my-tasks' | 'team-tasks';
  /** Selected inbox statuses; empty = open tasks only. */
  status: TaskInboxStatus[];
  assignee: string | 'all';
  search: string;
  sortBy: 'dueDate' | 'priority' | 'status' | 'created';
  sortDirection: 'asc' | 'desc';
  showOverdueOnly: boolean;
}

export const defaultTaskFilters: TaskFilters = {
  provider: ['mine'],
  taskType: [],
  status: [],
  assignee: 'all',
  search: '',
  sortBy: 'dueDate',
  sortDirection: 'asc',
  showOverdueOnly: false,
  view: 'my-tasks',
};

export type TaskViewMode = 'table' | 'cards';

export interface TaskWithUI extends Task {
  isOverdue?: boolean;
  isDueToday?: boolean;
  isDueThisWeek?: boolean;
  assigneeDisplayName?: string;
  patientDisplayName?: string;
}

export interface TaskFormValues {
  description: string;
  status: TaskInboxStatus;
  priority: TaskPriority;
  dueDate: string | null;
  assignee: Reference | null;
  patient: Reference | null;
  notes: string;
}

export type TaskBulkAction = 'updateStatus' | 'updatePriority' | 'updateAssignee' | 'delete';
